import React, { useEffect, useMemo, useState } from "react";
import {
  FiDownload,
  FiCheck,
  FiX,
  FiClock,
  FiFileText,
  FiPhone,
  FiMail,
} from "react-icons/fi";
import { ConfirmationDialog, PageHeader, useToast } from "components/admin";
import { Input, Select, Table } from "components/common";
import {
  deleteAdminRecruitmentApplication,
  getAdminRecruitmentApplications,
  updateAdminRecruitmentApplication,
} from "apis/adminRecruitmentApplications";
import {
  useAdminDeleteMutation,
  useAdminListQuery,
  useAdminUpdateMutation,
} from "hooks";
import "../../../styles/admin-shared.scss";
import { normalizeUploadedImageUrl } from "apis/uploadImage";

const RecruitmentApplicationsList = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(10);
  const [keyword, setKeyword] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [deleteId, setDeleteId] = useState(null);
  const toast = useToast();

  const queryParams = useMemo(
    () => ({
      pageNumber: currentPage,
      pageSize,
      keyword: keyword || undefined,
      status: statusFilter || undefined,
    }),
    [currentPage, pageSize, keyword, statusFilter],
  );

  const {
    data = { items: [], total: 0, currentPage, pageSize },
    isFetching,
    error,
  } = useAdminListQuery(
    "recruitment-applications",
    queryParams,
    getAdminRecruitmentApplications,
  );
  const updateMutation = useAdminUpdateMutation(
    "recruitment-applications",
    updateAdminRecruitmentApplication,
  );
  const deleteMutation = useAdminDeleteMutation(
    "recruitment-applications",
    deleteAdminRecruitmentApplication,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [keyword, statusFilter]);
  useEffect(() => {
    if (error) toast.error(error.message || "Tải danh sách hồ sơ CV thất bại");
  }, [error, toast]);

  const handleStatusChange = async (id, newStatus) => {
    try {
      const res = await updateMutation.mutateAsync({
        id,
        data: { status: newStatus },
      });
      if (res?.success) {
        toast.success("Cập nhật trạng thái thành công");
      } else {
        toast.error(res?.message || "Cập nhật thất bại");
      }
    } catch {
      toast.error("Có lỗi xảy ra khi cập nhật");
    }
  };

  const handleDelete = async () => {
    try {
      const res = await deleteMutation.mutateAsync(deleteId);
      if (res?.success) {
        toast.success("Xóa hồ sơ thành công");
      } else {
        toast.error(res?.message || "Xóa thất bại");
      }
    } catch {
      toast.error("Xóa thất bại");
    }
    setDeleteId(null);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleString("vi-VN");
  };

  const getStatusBadge = (status) => {
    const s = (status || "pending").toLowerCase();
    switch (s) {
      case "reviewed":
      case "contacted":
        return <span className="status-badge active">Đã liên hệ</span>;
      case "rejected":
        return <span className="status-badge expired">Từ chối</span>;
      default:
        return <span className="status-badge warning">Chờ duyệt</span>;
    }
  };

  const columns = [
    {
      header: "ID",
      render: (row) => row.Id ?? row.id ?? "—",
    },
    {
      header: "Ứng viên",
      render: (row) => (
        <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "14.5px" }}>
          {row.FullName || row.fullName || "—"}
        </div>
      ),
    },
    {
      header: "Liên hệ",
      render: (row) => (
        <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
          <div
            style={{
              fontSize: "16px",
              color: "#0f172a",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiPhone size={17} style={{ color: "#16a34a", flexShrink: 0 }} />
            <strong
              style={{
                fontSize: "16px",
                fontWeight: 700,
                letterSpacing: "0.4px",
              }}
            >
              {row.Phone || row.phone || "—"}
            </strong>
          </div>
          <div
            style={{
              fontSize: "13.5px",
              color: "#64748b",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <FiMail size={15} style={{ color: "#2563eb", flexShrink: 0 }} />
            <span>{row.Email || row.email || "—"}</span>
          </div>
        </div>
      ),
    },
    {
      header: "Vị trí ứng tuyển",
      render: (row) => {
        if (row.recruitmentTitle || row.RecruitmentTitle) {
          return row.recruitmentTitle || row.RecruitmentTitle;
        }
        const recruitment = row.Recruitments || row.recruitments;
        const translations =
          recruitment?.RecruitmentTranslations ||
          recruitment?.recruitmentTranslations ||
          recruitment?.translations;
        const translation = Array.isArray(translations)
          ? translations[0]
          : null;
        return (
          translation?.Title ||
          translation?.title ||
          recruitment?.title ||
          "Ứng tuyển chung"
        );
      },
    },
    {
      header: "File CV",
      render: (row) => {
        const cvUrl = row.CvFileUrl || row.cvFileUrl;
        if (!cvUrl) return "—";
        const fullUrl = cvUrl.startsWith("http")
          ? cvUrl
          : normalizeUploadedImageUrl(cvUrl);
        return (
          <a
            href={fullUrl}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "6px",
              padding: "6px 12px",
              background: "#eff6ff",
              color: "#2563eb",
              borderRadius: "8px",
              fontWeight: 600,
              textDecoration: "none",
            }}
          >
            <FiFileText size={14} /> Xem CV
          </a>
        );
      },
    },
    {
      header: "Ngày nộp",
      render: (row) => formatDate(row.CreatedAt || row.createdAt),
    },
    {
      header: "Trạng thái",
      render: (row) => getStatusBadge(row.Status || row.status),
    },
    {
      header: "Thao tác",
      render: (row) => {
        const id = row.Id ?? row.id;
        const currentStatus = (
          row.Status ||
          row.status ||
          "pending"
        ).toLowerCase();
        return (
          <div className="action-buttons">
            {currentStatus !== "contacted" && (
              <button
                className="icon-btn icon-btn--edit"
                style={{ background: "#e8f8f0", color: "#2e7d32" }}
                onClick={() => handleStatusChange(id, "contacted")}
                title="Đánh dấu đã liên hệ"
              >
                <FiCheck size={15} />
              </button>
            )}
            {currentStatus !== "rejected" && (
              <button
                className="icon-btn icon-btn--delete"
                onClick={() => handleStatusChange(id, "rejected")}
                title="Đánh dấu từ chối"
              >
                <FiX size={15} />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div className="admin-page">
      <PageHeader
        title="Quản lý Hồ sơ CV"
        subtitle="Danh sách hồ sơ và CV nộp trực tuyến từ ứng viên"
      />
      <div className="filters-bar">
        <div className="filter-item">
          <Input
            placeholder="Tìm theo tên ứng viên, email, SĐT..."
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
        </div>
        <div className="filter-item">
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={[
              { value: "", label: "Tất cả trạng thái" },
              { value: "pending", label: "Chờ duyệt" },
              { value: "contacted", label: "Đã liên hệ" },
              { value: "rejected", label: "Từ chối" },
            ]}
          />
        </div>
      </div>
      <Table
        columns={columns}
        data={data.items}
        loading={isFetching}
        pagination={{
          currentPage: data.currentPage,
          totalPages: Math.ceil(data.total / data.pageSize) || 1,
          onPageChange: setCurrentPage,
        }}
      />
      <ConfirmationDialog
        open={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="Xóa Hồ sơ CV"
        message="Bạn có chắc chắn muốn xóa hồ sơ ứng viên này không?"
      />
    </div>
  );
};

export default RecruitmentApplicationsList;
